/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/getRecipThresholds.js
 *
 * RECIP 1.0 per-timepoint SUV threshold computation.
 *
 * Two thresholds per timepoint:
 *
 *   bone        constant 3.0  (default; configurable via criteria.boneSuvCutoff)
 *
 *   softTissue  (liverFormulaConstant / SUVmean_liver) × (SUVmean_liver + SD_liver)
 *               null if no liver-reference ROI or no SUV data at this tp.
 *
 * Two lookup variants:
 *
 *   computeRecipThresholds(ctSeriesUid, ssUid, criteria, allRois)
 *     Full key lookup (ssUid required). Used by applyPsmaClassificationFromBridge
 *     and the MeasurementSection threshold banner, both of which have imageSets.
 *
 *   computeRecipThresholdsFromPartialKey(ctSeriesUid, criteria, allRois)
 *     Partial key lookup (ssUid NOT required). Uses getSuvByPartialKey so
 *     getRecipAssessments can self-heal when the caller omits the 5th thresholds
 *     argument. Safe in single-RTSTRUCT-multi-CT (the typical setup) because
 *     every (roiNumber, ctSeriesUid) pair maps to exactly one bridge entry.
 */

import {
  makeSuvKey,
  getSuvByKey,
  getSuvByPartialKey,
} from '../suv/suvViewportBridge';
import { TRACKING_TYPES } from './guideline';

/** Default liver ROI name patterns used when criteria.liverROINames is absent. */
const DEFAULT_LIVER_NAMES = [
  'right liver lobe',
  'liver reference',
  'liver',
  'right hepatic lobe',
];

/**
 * Compute RECIP 1.0 thresholds for one timepoint.
 * Uses the full bridge key — requires ssUid (structureSet UID).
 *
 * @param {string} ctSeriesUid
 * @param {string} ssUid
 * @param {object} criteria
 * @param {Array}  allRois
 * @returns {{ bone: number, softTissue: number|null, liverInputs: object|null }}
 */
export function computeRecipThresholds(ctSeriesUid, ssUid, criteria, allRois) {
  const thresholds = {
    bone: criteria?.boneSuvCutoff ?? 3.0,
    softTissue: null,
    liverInputs: null,
  };

  const liverRois = allRois.filter(
    r => r.trackingType === TRACKING_TYPES.LIVER_REFERENCE
  );
  if (!liverRois.length) return thresholds;

  if (liverRois.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      '[RECIP] Multiple liver-reference ROIs — using first for threshold computation.'
    );
  }
  const liverRoi = liverRois[0];

  const liverKey = makeSuvKey(ssUid, liverRoi.ROINumber, ctSeriesUid);
  const liverEntry = getSuvByKey(liverKey);
  const liverSuv = liverEntry?.suv ?? liverEntry;

  if (
    !liverSuv ||
    liverSuv.suvMean == null ||
    !isFinite(liverSuv.suvMean) ||
    liverSuv.suvSD == null ||
    !isFinite(liverSuv.suvSD)
  ) {
    return thresholds;
  }

  const c = criteria?.liverFormulaConstant ?? 4.3;
  const { suvMean, suvSD } = liverSuv;
  thresholds.softTissue = (c / suvMean) * (suvMean + suvSD);
  thresholds.liverInputs = {
    suvMean,
    suvSD,
    formula: c,
    roiName: liverRoi.ROIName,
    roiNumber: liverRoi.ROINumber,
  };

  return thresholds;
}

/**
 * Compute RECIP 1.0 thresholds for one timepoint WITHOUT knowing ssUid.
 * Uses getSuvByPartialKey (`:roiNumber:ctSeriesUid` suffix match).
 *
 * Designed for getRecipAssessments self-healing — when the calling site hasn't
 * passed the pre-computed thresholds object, this lets the response calculator
 * read directly from the bridge without needing imageSets.
 *
 * Safe in single-RTSTRUCT-multi-CT (typical setup) because
 * getSuvByPartialKey returns the unique match. Logs a warning if ambiguous.
 *
 * @param {string} ctSeriesUid
 * @param {object} criteria
 * @param {Array}  allRois
 * @returns {{ bone: number, softTissue: number|null, liverInputs: object|null }}
 */
export function computeRecipThresholdsFromPartialKey(
  ctSeriesUid,
  criteria,
  allRois
) {
  const thresholds = {
    bone: criteria?.boneSuvCutoff ?? 3.0,
    softTissue: null,
    liverInputs: null,
  };

  const liverRois = allRois.filter(
    r => r.trackingType === TRACKING_TYPES.LIVER_REFERENCE
  );
  if (!liverRois.length) return thresholds;

  const liverRoi = liverRois[0];
  // Partial key: empty ssUid leg → matches any ssUid for this roiNumber + ctSeriesUid
  const partialKey = `:${liverRoi.ROINumber}:${ctSeriesUid}`;
  const liverEntry = getSuvByPartialKey(partialKey);
  const liverSuv = liverEntry?.suv ?? liverEntry;

  if (
    !liverSuv ||
    liverSuv.suvMean == null ||
    !isFinite(liverSuv.suvMean) ||
    liverSuv.suvSD == null ||
    !isFinite(liverSuv.suvSD)
  ) {
    return thresholds;
  }

  const c = criteria?.liverFormulaConstant ?? 4.3;
  const { suvMean, suvSD } = liverSuv;
  thresholds.softTissue = (c / suvMean) * (suvMean + suvSD);
  thresholds.liverInputs = {
    suvMean,
    suvSD,
    formula: c,
    roiName: liverRoi.ROIName,
    roiNumber: liverRoi.ROINumber,
  };

  return thresholds;
}

/**
 * Compute thresholds for ALL timepoints in an imageSets array (full key).
 * Used by applyPsmaClassificationFromBridge and MeasurementSection banner.
 *
 * @param {Array}  imageSets
 * @param {Array}  allRois
 * @param {object} criteria
 * @returns {{ [ctSeriesUid]: { bone, softTissue, liverInputs } }}
 */
export function computeRecipThresholdsAllTps(imageSets, allRois, criteria) {
  const result = {};
  for (const ctSet of imageSets) {
    if (!ctSet) continue;
    const ctSeriesUid = ctSet.SeriesInstanceUID;
    const ssUid = ctSet.rtstructSeriesInstanceUID;
    if (!ctSeriesUid || !ssUid) continue;
    result[ctSeriesUid] = computeRecipThresholds(
      ctSeriesUid,
      ssUid,
      criteria,
      allRois
    );
  }
  return result;
}

/** Export default liver patterns so classifiers can use them as fallback. */
export { DEFAULT_LIVER_NAMES };
